import * as z from "zod";
import { uuidV7 } from "../index";
import { oauthClientKinds } from "./enums";

// The connections of an assistant (P5.15b, ADR-263): OAuth 2.1 for the MCP server of Nexxt (`gateway-mcp`), whose
// authorization server keeps its state in identity. A connection is a delegated access (ADR-262) made by the consent
// of a user; its tokens are opaque, kept as HMAC, and lead back to it. What the gateway, identity and Backstage share:
// the constants of the protocol, the rules on redirects and scopes, the inputs and the answers.

// --- The protocol ---

/** The scopes of the server: what the client asks; the permissions chosen at the consent are what the token carries. */
export const mcpScopes = { read: "mcp:read", write: "mcp:write", offline: "offline_access" } as const;
export const supportedScopes = [mcpScopes.read, mcpScopes.write, mcpScopes.offline] as const;
export type McpScope = (typeof supportedScopes)[number];

/** An access token lives an hour: Claude refreshes it before the end (docs of Claude, "Token refresh"). */
export const ACCESS_TOKEN_SECONDS = 3600;
/** A refresh token lives 30 days from its issue; each use rotates it, and the connection ends 30 days after its last. */
export const REFRESH_TOKEN_DAYS = 30;
/** A code of authorization: single use, a minute. */
export const AUTHORIZATION_CODE_SECONDS = 60;
/** Live connections of one user: one more is refused until one is revoked or ends. */
export const MAX_ACTIVE_CONNECTIONS = 20;

/** The callback of the apps of Claude (web, desktop, mobile, Cowork), matched exactly. */
export const CLAUDE_CALLBACK = "https://claude.ai/api/mcp/auth_callback";

/**
 * The Client ID Metadata Documents of Claude (docs of Claude, "DCR and CIMD details"), admitted by migration: the apps
 * and Claude Code. Any other client is admitted from Backstage.
 */
export const claudeClientIds = {
  app: "https://claude.ai/oauth/mcp-oauth-client-metadata",
  code: "https://claude.ai/oauth/claude-code-client-metadata",
} as const;

/** The path of the MCP endpoint on its gateway: the resource is the address of the gateway with it. */
export const MCP_PATH = "/mcp";

/** The page of Backstage that tells how to connect Claude, with the address of the server. */
export const connectClaudePath = "/settings/claude";

// --- Redirects (RFC 8252, OAuth 2.1 §2.3.1) ---

const LOOPBACK_HOSTS = new Set(["localhost", "127.0.0.1", "[::1]"]);

function parsedUrl(value: string): URL | null {
  try {
    return new URL(value);
  } catch {
    return null;
  }
}

/** `http://localhost`, `http://127.0.0.1` or `http://[::1]` on any port: the redirect of a native client. */
export function isLoopbackRedirect(uri: string): boolean {
  const url = parsedUrl(uri);
  return url !== null && url.protocol === "http:" && LOOPBACK_HOSTS.has(url.hostname) && !url.username && !url.hash;
}

/**
 * Whether the redirect asked for is one of those of the client: the same text, or a loopback one that differs only by
 * its port (RFC 8252 §7.3; also for `localhost`, which Claude Code declares in its document).
 */
export function redirectMatches(registered: readonly string[], requested: string): boolean {
  if (registered.includes(requested)) return true;
  if (!isLoopbackRedirect(requested)) return false;
  const asked = parsedUrl(requested);
  return registered.some((uri) => {
    const known = isLoopbackRedirect(uri) ? parsedUrl(uri) : null;
    return (
      known !== null &&
      asked !== null &&
      known.hostname === asked.hostname &&
      known.pathname === asked.pathname &&
      known.search === asked.search
    );
  });
}

/**
 * The redirects a client may register by itself (DCR): the callback of Claude, exactly, or a loopback one for a client
 * on the computer of the user. Anything else is a client to admit from Backstage with its document.
 */
export const registrableRedirect = (uri: string): boolean => uri === CLAUDE_CALLBACK || isLoopbackRedirect(uri);

/**
 * A host the gateway never fetches a document from: an address instead of a name (the URL parser writes every form of
 * an IPv4 address as four numbers, and IPv6 in brackets), a name of one label, or a name of a private network.
 */
function isPrivateHost(hostname: string): boolean {
  return (
    hostname.startsWith("[") ||
    /^\d+\.\d+\.\d+\.\d+$/.test(hostname) ||
    !hostname.includes(".") ||
    /\.(localhost|local|internal|lan|home\.arpa)$/.test(hostname)
  );
}

/**
 * A `client_id` that is the URL of a metadata document: https on the default port, a public host name, a path, no
 * fragment, no credentials. The gateway fetches it, so it never names an address of a private network (SSRF).
 */
export function isMetadataDocumentUrl(clientId: string): boolean {
  const url = parsedUrl(clientId);
  return (
    url !== null &&
    url.protocol === "https:" &&
    !url.port &&
    !isPrivateHost(url.hostname) &&
    url.pathname.length > 1 &&
    !url.hash &&
    !url.username &&
    !url.password &&
    clientId.length <= 500
  );
}

/**
 * Whether a redirect of a metadata document is on the origin of its URL, or loopback: a document cannot send the
 * codes of its users to a site that is not its own.
 */
export function sameOriginRedirect(clientId: string, uri: string): boolean {
  if (isLoopbackRedirect(uri)) return true;
  const client = parsedUrl(clientId);
  const target = parsedUrl(uri);
  return client !== null && target !== null && target.protocol === "https:" && target.origin === client.origin;
}

/** The host a consent page names for the redirect (MCP 2025-11-25, "Localhost Redirect URI Risks"). */
export const redirectHost = (uri: string): string => parsedUrl(uri)?.host ?? uri;

// --- Scopes ---

/**
 * The scopes granted for what the client asked: the ones the server knows, `offline_access` always (the refresh token
 * is what keeps a connection alive). A client that names no scope of the server gets both: the consent decides.
 */
export function grantedScopes(requested: string | null | undefined): McpScope[] {
  const asked = new Set((requested ?? "").split(" ").filter(Boolean));
  const mcp = [mcpScopes.read, mcpScopes.write].filter((scope) => asked.has(scope));
  const chosen: McpScope[] = mcp.length > 0 ? mcp : [mcpScopes.read, mcpScopes.write];
  if (chosen.includes(mcpScopes.write) && !chosen.includes(mcpScopes.read)) chosen.unshift(mcpScopes.read);
  return [...chosen, mcpScopes.offline];
}

/** Whether a connection may carry permissions that change data: its client asked for `mcp:write`. */
export const scopesAllowWriting = (scopes: readonly string[]): boolean => scopes.includes(mcpScopes.write);

// --- What gateway-mcp asks identity ---

const clientIdText = z.string().trim().min(1).max(500);
const redirectUri = z.string().max(500);
const source = {
  /** `CF-Connecting-IP` of the request. */
  ip: z.union([z.ipv4(), z.ipv6()]).nullable().catch(null),
  /** `User-Agent` of the request, cut by identity. */
  client: z.string().max(1000).nullable(),
};

/** A client as the gateway and Backstage see it: never a secret, there is none (public clients only). */
export const oauthClientDto = z
  .object({
    id: z.uuid(),
    clientId: z.string(),
    kind: z.enum(oauthClientKinds),
    name: z.string(),
    /** The redirects it registered; empty for a document, whose redirects the gateway reads at each authorization. */
    redirectUris: z.array(z.string()),
    enabled: z.boolean(),
    createdAt: z.iso.datetime(),
    lastUsedAt: z.iso.datetime().nullable(),
  })
  .meta({ id: "OAuthClient" });
export type OAuthClientDto = z.output<typeof oauthClientDto>;

/** DCR (RFC 7591), from `/oauth/register`: only redirects that `registrableRedirect` accepts. */
export const registerOAuthClientInput = z.object({
  redirectUris: z.array(redirectUri).min(1).max(10),
  clientName: z.string().trim().max(200).nullable().optional(),
});
export type RegisterOAuthClientInput = z.input<typeof registerOAuthClientInput>;

export const getOAuthClientInput = z.object({ clientId: clientIdText });
export type GetOAuthClientInput = z.input<typeof getOAuthClientInput>;

/** The page of consent of a signed-in user for a client. */
export const getConnectionConsentInput = z.object({
  userId: z.uuid(),
  clientId: clientIdText,
  scopes: z.array(z.enum(supportedScopes)).max(3),
});
export type GetConnectionConsentInput = z.input<typeof getConnectionConsentInput>;

/** Why a user cannot connect an assistant now: the page of the gateway says it in plain words. */
export const connectionRefusals = ["not_allowed", "blocked", "disabled", "limit_reached", "client_disabled"] as const;
export type ConnectionRefusal = (typeof connectionRefusals)[number];

export const connectionConsentDto = z.object({
  user: z.object({ id: z.uuid(), name: z.string().nullable(), email: z.string() }),
  /** Null when the user may connect the client now. */
  refusal: z.enum(connectionRefusals).nullable(),
  /** The client asked for `mcp:write`: the page offers the permissions that change data too. */
  writesOffered: z.boolean(),
  /** What the user may give the connection now, by area: what a role of its delegates to connections. */
  permissions: z.array(z.object({ key: z.string(), module: z.string(), description: z.string().nullable() })),
});
export type ConnectionConsentDto = z.output<typeof connectionConsentDto>;

/** The consent given: the connection and a code of authorization for it. */
export const approveConnectionInput = z.object({
  id: uuidV7,
  userId: z.uuid(),
  clientId: clientIdText,
  /** The name the page showed: the host of a metadata document, or the name a registered client gave. */
  clientName: z.string().trim().min(1).max(200),
  redirectUri,
  /** PKCE S256 (RFC 7636): 43 characters of base64url. */
  codeChallenge: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
  resource: z.string().max(500),
  scopes: z.array(z.enum(supportedScopes)).min(1).max(3),
  permissions: z.array(z.string().trim().min(1).max(100)).min(1).max(300),
  ...source,
});
export type ApproveConnectionInput = z.input<typeof approveConnectionInput>;

export const authorizationCodeDto = z.object({ code: z.string(), accessId: z.uuid() });
export const revokedTokenDto = z.object({ revoked: z.boolean() });

/** `grant_type=authorization_code` of `/oauth/token`. */
export const exchangeAuthorizationCodeInput = z.object({
  code: z.string().max(100),
  clientId: clientIdText,
  redirectUri,
  /** RFC 7636 §4.1: 43 to 128 characters. */
  codeVerifier: z.string().min(43).max(128),
  /** RFC 8707: when the client sends it, it must be the resource the code was given for. */
  resource: z.string().max(500).nullable(),
  ...source,
});
export type ExchangeAuthorizationCodeInput = z.input<typeof exchangeAuthorizationCodeInput>;

/** `grant_type=refresh_token` of `/oauth/token`: the token is rotated at every use. */
export const refreshConnectionTokenInput = z.object({
  refreshToken: z.string().max(100),
  clientId: clientIdText,
  resource: z.string().max(500).nullable(),
  ...source,
});
export type RefreshConnectionTokenInput = z.input<typeof refreshConnectionTokenInput>;

/** The answer of `/oauth/token` (RFC 6749 §5.1), in the words of the contract. */
export const oauthTokensDto = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresIn: z.number().int(),
  scope: z.string(),
});
export type OAuthTokensDto = z.output<typeof oauthTokensDto>;

/** RFC 7009: the client revokes an access or a refresh token, and with it its connection. */
export const revokeOAuthTokenInput = z.object({
  token: z.string().max(100),
  clientId: clientIdText.nullable(),
  ...source,
});
export type RevokeOAuthTokenInput = z.input<typeof revokeOAuthTokenInput>;

// --- What Backstage does ---

/** The holder changes what a connection may do, without connecting again, within what its client asked for. */
export const updateConnectionPermissionsBody = z.object({
  permissions: z.array(z.string().trim().min(1).max(100)).min(1).max(300),
});
export const updateConnectionPermissionsInput = updateConnectionPermissionsBody.extend({ accessId: z.uuid() });
export type UpdateConnectionPermissionsInput = z.input<typeof updateConnectionPermissionsInput>;

export const listOAuthClientsInput = z.object({});
export type ListOAuthClientsInput = z.input<typeof listOAuthClientsInput>;
export const oauthClientListDto = z.object({ items: z.array(oauthClientDto) });

/** A client admitted by the administrator: the URL of its metadata document and the name it is known by. */
export const admitOAuthClientInput = z.object({
  clientId: clientIdText.refine(isMetadataDocumentUrl, "the https URL of a metadata document"),
  name: z.string().trim().min(1).max(200),
});
export type AdmitOAuthClientInput = z.input<typeof admitOAuthClientInput>;

export const setOAuthClientEnabledBody = z.object({ enabled: z.boolean() });
export const setOAuthClientEnabledInput = setOAuthClientEnabledBody.extend({ id: z.uuid() });
export type SetOAuthClientEnabledInput = z.input<typeof setOAuthClientEnabledInput>;
export const oauthClientParams = setOAuthClientEnabledInput.pick({ id: true });

/** The access via MCP turned off for one user: no new token or connection, and every call refused. */
export const setDelegationBlockBody = z.object({
  blocked: z.boolean(),
  reason: z.string().trim().min(1).max(500).optional(),
});
export const setDelegationBlockInput = setDelegationBlockBody.extend({ userId: z.uuid() });
export type SetDelegationBlockInput = z.input<typeof setDelegationBlockInput>;
export const delegationBlockParams = setDelegationBlockInput.pick({ userId: true });
/** `changed`: false when the switch was in that position already. */
export const delegationBlockStateDto = z.object({ userId: z.uuid(), blocked: z.boolean(), changed: z.boolean() });

export const delegationBlockDto = z
  .object({
    user: z.object({ id: z.uuid(), name: z.string().nullable(), email: z.string() }),
    reason: z.string().nullable(),
    blockedAt: z.iso.datetime(),
    blockedBy: z.object({ id: z.uuid(), name: z.string().nullable() }).nullable(),
  })
  .meta({ id: "DelegationBlock" });
export const delegationBlockListDto = z.object({ items: z.array(delegationBlockDto) });

export const listDelegationBlocksInput = z.object({});
export type ListDelegationBlocksInput = z.input<typeof listDelegationBlocksInput>;

// --- Errors ---

/**
 * The codes of the authorization server (RFC 6749 §5.2, RFC 7591 §3.2.2, RFC 8707): gateway-mcp answers them as they
 * are, in the body of `/oauth/token` and `/oauth/register` or on the redirect.
 */
export type OAuthErrorCode =
  | "invalid_request"
  | "invalid_client"
  | "invalid_grant"
  | "unauthorized_client"
  | "unsupported_grant_type"
  | "unsupported_response_type"
  | "invalid_scope"
  | "invalid_target"
  | "access_denied"
  | "invalid_redirect_uri"
  | "invalid_client_metadata"
  | "temporarily_unavailable";

/** Codes of Backstage for the connections and the clients, each with a text in the app. */
export type ConnectionErrorCode =
  /** A permission the connection may not carry: not delegated to connections, or one that changes data without `mcp:write`. */
  | "connection_permissions_invalid"
  /** A client already admitted, by its URL. */
  | "oauth_client_exists";
